import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Lead } from '../types/Lead';
import { searchLeads, SearchOptions, SEARCH_CONFIG } from '../utils/searchUtils';

/**
 * Custom hook for managing search functionality with performance optimizations
 */

// Type definitions
interface UseSearchReturn {
  readonly searchQuery: string;
  readonly filteredLeads: readonly Lead[];
  readonly isSearching: boolean;
  readonly resultsCount: number;
  readonly isLoading: boolean;
  readonly error: string | null;
  readonly setSearchQuery: (query: string) => void;
  readonly clearSearch: () => void;
  readonly setSearchOptions: (options: SearchOptions) => void;
}

interface UseSearchOptions extends SearchOptions {
  readonly enableLoading?: boolean;
  readonly maxResults?: number;
}

// Default options with better type safety
const DEFAULT_SEARCH_OPTIONS: Required<Pick<UseSearchOptions, 'caseSensitive' | 'exactMatch' | 'enableLoading'>> = {
  caseSensitive: SEARCH_CONFIG.DEFAULT_CASE_SENSITIVE,
  exactMatch: SEARCH_CONFIG.DEFAULT_EXACT_MATCH,
  enableLoading: false,
} as const;

export const useSearch = (
  leads: readonly Lead[], 
  initialOptions: UseSearchOptions = {}
): UseSearchReturn => {
  // State management
  const [searchQuery, setSearchQueryState] = useState<string>('');
  const [searchOptions, setSearchOptionsState] = useState<SearchOptions>({
    ...DEFAULT_SEARCH_OPTIONS,
    ...initialOptions
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Refs for cleanup and optimization
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const previousLeadsRef = useRef<readonly Lead[]>(leads);
  const previousQueryRef = useRef<string>('');

  // Cleanup function
  const cleanup = useCallback(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  // Memoized filtered results with error handling
  const filteredLeads = useMemo(() => {
    try {
      setError(null);

      // Early return for empty queries
      if (!searchQuery?.trim()) {
        return leads;
      }

      // Validate inputs
      if (!Array.isArray(leads)) {
        throw new Error('Invalid leads array provided');
      }

      const results = searchLeads(leads, searchQuery, searchOptions);
      
      // Apply max results limit if specified
      const maxResults = (searchOptions as UseSearchOptions).maxResults;
      return maxResults && results.length > maxResults 
        ? results.slice(0, maxResults) 
        : results;

    } catch (searchError) {
      const errorMessage = searchError instanceof Error 
        ? searchError.message 
        : 'An error occurred while searching';
      
      console.error('Search error:', searchError);
      setError(errorMessage);
      return leads; // Fallback to original leads
    }
  }, [leads, searchQuery, searchOptions]);

  // Derived state with proper type safety
  const isSearching = useMemo(() => 
    typeof searchQuery === 'string' && searchQuery.trim().length > 0, 
    [searchQuery]
  );

  const resultsCount = useMemo(() => 
    Array.isArray(filteredLeads) ? filteredLeads.length : 0, 
    [filteredLeads]
  );

  // Optimized search query setter with debouncing option
  const setSearchQuery = useCallback((query: string) => {
    if (typeof query !== 'string') {
      console.warn('Invalid query type provided to setSearchQuery');
      return;
    }

    const enableLoading = (searchOptions as UseSearchOptions).enableLoading;
    
    if (enableLoading) {
      setIsLoading(true);
      cleanup();
      
      searchTimeoutRef.current = setTimeout(() => {
        setSearchQueryState(query);
        setIsLoading(false);
      }, 150); // Small delay for loading state
    } else {
      setSearchQueryState(query);
    }

    previousQueryRef.current = query;
  }, [searchOptions, cleanup]);

  // Clear search with proper cleanup
  const clearSearch = useCallback(() => {
    cleanup();
    setSearchQueryState('');
    setError(null);
    setIsLoading(false);
    previousQueryRef.current = '';
  }, [cleanup]);

  // Search options setter with validation
  const setSearchOptions = useCallback((options: SearchOptions) => {
    if (!options || typeof options !== 'object') {
      console.warn('Invalid search options provided');
      return;
    }

    setSearchOptionsState(prevOptions => ({
      ...prevOptions,
      ...options
    }));
  }, []);

  // Update refs for optimization
  useEffect(() => {
    previousLeadsRef.current = leads;
  }, [leads]);

  return {
    searchQuery,
    filteredLeads,
    isSearching,
    resultsCount,
    isLoading,
    error,
    setSearchQuery,
    clearSearch,
    setSearchOptions
  } as const;
};